"use client";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";

import {
  CalendarIcon,
  ChevronDown,
  ChevronUp,
  Copy,
  EllipsisVertical,
  Trash2,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Field } from "@/shared/components/ui/field";
import { Label } from "@/shared/components/ui/label";
import { v4 } from "uuid";
import { format, Locale } from "date-fns";
import { de } from "date-fns/locale";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { ReactNode } from "react";
import { InputDebounced } from "../inputs/input-debounced";
import { TextareaDebounced } from "../inputs/textarea-debounced";
import { Textarea } from "../ui/textarea";
import { DebounceProps } from "../field/field";
import { useDecimalInputProps } from "../inputs/decimal-input";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";

/**
 * The error props of the ETable cells taking input.
 */
type ETableCellErrorProps = {
  /**
   * Marks the cell as invalid. Use it for errors of another source; it is
   * combined with the errors of `fieldName`.
   */
  hasError?: boolean;
  /**
   * The key of the cell's field in the nearest
   * ErrValidationFailedContextProvider, e.g. `positions.${id}.title`. The cell
   * is marked invalid while the key has errors and resets them whenever the
   * user changes the value.
   */
  fieldName?: string;
};

// useCellErrors combines the `hasError` prop with the errors of the
// `fieldName` in the ErrValidationFailedContext.
function useCellErrors({ hasError, fieldName }: ETableCellErrorProps) {
  const { invalid, reset } = useFieldErrors(fieldName);
  return { invalid: invalid || !!hasError, reset };
}

const invalidCellClassName = "inset-ring inset-ring-red-500";

/**
 * ETable is a table component with prepared cells for input.
 */
function ETable({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto"
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom border text-sm", className)}
        {...props}
      />
    </div>
  );
}

function ETableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("[&_tr]:border-b", className)}
      {...props}
    />
  );
}

function ETableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", "[&_tr>*]:py-0", className)}
      {...props}
    />
  );
}

function ETableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "bg-muted/50 border-t font-medium [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  );
}

function ETableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "data-[state=selected]:bg-muted border-b transition-colors",
        className,
      )}
      {...props}
    />
  );
}

function ETableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "text-muted-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        "border", // suits bordered design
        className,
      )}
      {...props}
    />
  );
}

function ETableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        "border-r border-l", // too much space otherwise.
        className,
      )}
      {...props}
    />
  );
}

function ETableCellCheckbox({
  label,
  hasError,
  fieldName,
  onCheckedChange,
  colSpan,
  ...props
}: React.ComponentProps<typeof CheckboxPrimitive.Root> &
  ETableCellErrorProps & {
    label?: string;
    colSpan?: number;
  }) {
  const id = v4();
  const { invalid, reset } = useCellErrors({ hasError, fieldName });
  return (
    <ETableCell
      colSpan={colSpan}
      // ETableCell drops the right padding of a cell holding a bare
      // checkbox; a label needs it back
      className={cn(label && "[&:has([role=checkbox])]:pr-2")}
    >
      <Field orientation="horizontal">
        <Checkbox
          id={id}
          aria-invalid={invalid || undefined}
          {...props}
          onCheckedChange={(checked) => {
            reset();
            onCheckedChange?.(checked);
          }}
        />
        {label && <Label htmlFor={id}>{label}</Label>}
      </Field>
    </ETableCell>
  );
}

/**
 * ETableCellInput provides a table cell working as a normal
 * input field.
 *
 * We use InputDebounce instead of Input here, since in the
 * table context we can expect large compontent trees regularly.
 * In the context of a generally debounced UI InputDebounce
 * helps pushing the need for more complex render performance
 * optimisations out.
 */
function ETableCellInput({
  className,
  colSpan,
  cellClassName,
  hasError,
  fieldName,
  onChange,
  ...props
}: React.ComponentProps<"input"> &
  ETableCellErrorProps & {
    colSpan?: number;
    cellClassName?: string;
  }) {
  const { invalid, reset } = useCellErrors({ hasError, fieldName });
  return (
    <ETableCell className={cn("px-0", cellClassName)} colSpan={colSpan}>
      <InputDebounced
        className={cn(
          "dark:hover:bg-accent dark:bg-background dark:focus-visible:bg-accent border-none focus-visible:border-none focus-visible:ring-0",
          "field-sizing-content shadow-none [&[type='number']]:pr-0 [&[type='number']]:text-right",
          invalid ? invalidCellClassName : "",
          className,
        )}
        aria-invalid={invalid || undefined}
        onBlur={(e) => {
          if (e.target.value !== e.target.defaultValue) {
            e.currentTarget.form?.requestSubmit();
          }
        }}
        {...props}
        onChange={(e) => {
          reset();
          onChange?.(e);
        }}
      />
    </ETableCell>
  );
}

/**
 * ETableCellDecimalInput is an {@link ETableCellInput} that always displays its
 * value padded to `precision` decimal places with a comma as the decimal
 * separator (German locale, e.g. `5` → `"5,00"`), including on initial load.
 *
 * It is {@link DecimalInput} in a table cell: same formatting, parsing and
 * re-sync behaviour, shared through {@link useDecimalInputProps}. Since
 * {@link ETableCellInput} buffers keystrokes, `onValueChange` is reported
 * debounced (drive your debounced save from there).
 *
 * @example
 * <ETableCellDecimalInput
 *   value={incidentalCost.materialFlatRate}
 *   onValueChange={setMaterialFlatRate}
 *   disabled={disabled}
 * />
 */
function ETableCellDecimalInput({
  value,
  onValueChange,
  precision,
  className,
  onFocus,
  onBlur,
  ...props
}: Omit<
  React.ComponentProps<typeof ETableCellInput>,
  "value" | "type" | "onChange"
> & {
  value: number;
  onValueChange: (value: number) => void;
  precision?: number;
}) {
  const decimalProps = useDecimalInputProps({
    value,
    onValueChange,
    precision,
    onFocus,
    onBlur,
  });

  return (
    <ETableCellInput
      {...props}
      {...decimalProps}
      className={cn("pr-4 text-right", className)}
    />
  );
}

function ETableCellSelect({
  className,
  label,
  placeholder,
  items,
  cellClassName,
  colSpan,
  rowSpan,
  hasError,
  fieldName,
  onValueChange,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root> &
  ETableCellErrorProps & {
    label: string;
    items: { value: string; label: ReactNode }[];
    cellClassName?: string;
    colSpan?: number;
    rowSpan?: number;
    className?: string;
    placeholder?: string;
  }) {
  const { invalid, reset } = useCellErrors({ hasError, fieldName });
  return (
    <ETableCell
      className={cn("px-0", cellClassName)}
      colSpan={colSpan}
      rowSpan={rowSpan}
    >
      <Select
        {...props}
        onValueChange={(value) => {
          reset();
          onValueChange?.(value);
        }}
      >
        <SelectTrigger
          aria-invalid={invalid || undefined}
          className={cn(
            "bg-background dark:bg-background dark:focus-visible:bg-accent w-full rounded-none border-none shadow-none focus-visible:border-none focus-visible:ring-0",
            invalid ? invalidCellClassName : "",
            className,
          )}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent align="center">
          <SelectGroup>
            <SelectLabel>{label}</SelectLabel>
            {items.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </ETableCell>
  );
}

/**
 * ETableCellMultiSelect provides a table cell picking any number of the
 * items. It reports the picked values in the order of the items. The
 * trigger lists the labels of the picked items, or the placeholder.
 */
function ETableCellMultiSelect<T extends string>({
  label,
  placeholder,
  items,
  values,
  onValuesChange,
  className,
  colSpan,
  rowSpan,
  disabled,
  hasError,
  fieldName,
}: ETableCellErrorProps & {
  label: string;
  placeholder?: string;
  items: { value: T; label: string }[];
  values: T[];
  onValuesChange: (values: T[]) => void;
  className?: string;
  colSpan?: number;
  rowSpan?: number;
  disabled?: boolean;
}) {
  const { invalid, reset } = useCellErrors({ hasError, fieldName });
  const picked = items.filter((item) => values.includes(item.value));
  const toggle = (value: T, checked: boolean) => {
    reset();
    onValuesChange(
      items
        .map((item) => item.value)
        .filter((v) => (v === value ? checked : values.includes(v))),
    );
  };

  return (
    <ETableCell className="px-0" colSpan={colSpan} rowSpan={rowSpan}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button
            variant="ghost"
            aria-invalid={invalid || undefined}
            className={cn(
              "bg-background dark:bg-background w-full justify-between rounded-none font-normal",
              invalid ? invalidCellClassName : "",
              className,
            )}
          >
            <span
              className={cn(picked.length === 0 && "text-muted-foreground")}
            >
              {picked.length === 0
                ? placeholder
                : picked.map((item) => item.label).join(", ")}
            </span>
            <ChevronDown className="opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          <DropdownMenuLabel>{label}</DropdownMenuLabel>
          {items.map((item) => (
            <DropdownMenuCheckboxItem
              key={item.value}
              checked={values.includes(item.value)}
              onCheckedChange={(checked) => toggle(item.value, checked)}
              // keeps the menu open to pick several items
              onSelect={(e) => e.preventDefault()}
            >
              {item.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </ETableCell>
  );
}

/**
 * ETableCellDatePicker provides a table cell picking a date. It reads and
 * reports the date as an ISO string, like {@link DatePickerField}, and
 * keeps the time of day of the moment it was picked, so a date never
 * silently jumps a day across time zones.
 *
 * The trigger carries no border, like {@link ETableCellSelect}, so a table
 * of dates stays calm to look at.
 */
function ETableCellDatePicker({
  className,
  date,
  onDateChange,
  cellClassName,
  colSpan,
  rowSpan,
  hasError,
  fieldName,
  disabled,
  placeholder,
  locale = de,
  weekStartsOn = 1,
}: ETableCellErrorProps & {
  date: string | null;
  onDateChange: (date: string) => void;
  className?: string;
  cellClassName?: string;
  colSpan?: number;
  rowSpan?: number;
  disabled?: boolean;
  placeholder?: string;
  locale?: Locale;
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
}) {
  const { invalid, reset } = useCellErrors({ hasError, fieldName });
  return (
    <ETableCell
      className={cn("px-0", cellClassName)}
      colSpan={colSpan}
      rowSpan={rowSpan}
    >
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            data-empty={!date}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            className={cn(
              "bg-background dark:bg-background dark:hover:bg-accent w-full justify-start rounded-none border-none px-3 text-left font-normal shadow-none",
              "data-[empty=true]:text-muted-foreground focus-visible:border-none focus-visible:ring-0",
              invalid ? invalidCellClassName : "",
              className,
            )}
          >
            <CalendarIcon />
            {date ? (
              format(new Date(date), "PPP", { locale })
            ) : (
              <span>{placeholder ?? t("Datum wählen")}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date ? new Date(date) : undefined}
            // Clearing the date is not offered: a cleared date is rejected
            // by the backends patching a date, so the current one is kept.
            onSelect={(newDate?: Date) => {
              if (!newDate) {
                return;
              }
              reset();
              onDateChange(setCurrentTime(newDate).toISOString());
            }}
            weekStartsOn={weekStartsOn}
          />
        </PopoverContent>
      </Popover>
    </ETableCell>
  );
}

// setCurrentTime keeps the time of day, so a date picked in a table does
// not jump a day when it is read in another time zone.
function setCurrentTime(date: Date): Date {
  const now = new Date();
  date.setHours(now.getHours());
  date.setMinutes(now.getMinutes());
  return date;
}

/**
 * ETableCellTextarea provides a table cell working as a textarea. It is
 * the multi-line counterpart of {@link ETableCellInput} and takes the same
 * props as {@link TextareaField}, so a field moved into a table keeps its
 * behaviour: keystrokes are buffered and reported once the user pauses,
 * unless `debounce` is false.
 *
 * The textarea grows with its content and cannot be resized by hand: a row
 * whose height the user can drag apart makes the table jump around.
 */
function ETableCellTextarea({
  className,
  colSpan,
  rowSpan,
  cellClassName,
  hasError,
  fieldName,
  debounce = true,
  delay,
  onChange,
  ...props
}: React.ComponentProps<"textarea"> &
  DebounceProps &
  ETableCellErrorProps & {
    colSpan?: number;
    rowSpan?: number;
    cellClassName?: string;
  }) {
  const { invalid, reset } = useCellErrors({ hasError, fieldName });
  const textareaClassName = cn(
    "dark:hover:bg-accent dark:bg-background dark:focus-visible:bg-accent border-none focus-visible:border-none focus-visible:ring-0",
    "min-h-9 resize-none py-1.5 shadow-none",
    invalid ? invalidCellClassName : "",
    className,
  );
  const textareaProps = {
    ...props,
    "aria-invalid": invalid || undefined,
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      reset();
      onChange?.(e);
    },
  };

  return (
    <ETableCell
      className={cn("px-0 align-top", cellClassName)}
      colSpan={colSpan}
      rowSpan={rowSpan}
    >
      {debounce ? (
        <TextareaDebounced
          className={textareaClassName}
          delay={delay}
          {...textareaProps}
        />
      ) : (
        <Textarea className={textareaClassName} {...textareaProps} />
      )}
    </ETableCell>
  );
}

function ETableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  );
}

/**
 * Props for ETableOptions component
 */
type ETableOptionsProps<T extends { id: string; order: number }> = {
  /** The data object of the current table row */
  item: T;
  /** The index of the current table row */
  rowIndex: number;
  /** The full list of objects of all table rows */
  items: T[];
  /** Callback for changes to the full list. */
  onItemsChange: (items: T[]) => void;
  /** A constructor to return a new object if a row is added.
   * If provided, the options "add row above / below" are shown. */
  newItem: () => T;
  /** A constructor to duplicate a given object.
   * If provided, the option "duplicate" is shown. */
  duplicate?: (item: T) => T;
  disabled?: boolean;
};

/**
 * ETableOptions offers a default set of operations to manipulate
 * the rows (and therefor items) in the table. This requires,
 * that the frontend has full control over item creation and deletion.
 */
function ETableOptions<T extends { id: string; order: number }>({
  item,
  rowIndex,
  items,
  onItemsChange,
  newItem,
  duplicate,
  disabled,
}: ETableOptionsProps<T>) {
  const doDelete = () => {
    onItemsChange(items.filter((i) => i.id !== item.id));
  };

  const insertRowAbove = () => {
    onItemsChange(addItem(items, newItem, rowIndex));
  };

  const insertRowBelow = () => {
    onItemsChange(addItem(items, newItem, rowIndex + 1));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button variant="ghost" size="sm" className="rounded-none">
          <EllipsisVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("Aktionen")}</DropdownMenuLabel>
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={insertRowAbove}>
            {t("Zeile darüber einfügen")}
            <DropdownMenuShortcut>
              <ChevronUp />
            </DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={insertRowBelow}>
            {t("Zeile darunter einfügen")}
            <DropdownMenuShortcut>
              <ChevronDown />
            </DropdownMenuShortcut>
          </DropdownMenuItem>
          {duplicate && (
            <DropdownMenuItem
              onClick={() =>
                onItemsChange(
                  addItem(items, () => duplicate(item), rowIndex + 1),
                )
              }
            >
              {t("Duplizieren")}
              <DropdownMenuShortcut>
                <Copy />
              </DropdownMenuShortcut>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={doDelete}>
            {t("Zeile löschen")}
            <DropdownMenuShortcut>
              <Trash2 />
            </DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function addItem<T extends { id: string; order: number }>(
  items: T[],
  newItem: () => T,
  index?: number,
): T[] {
  if (index === undefined || index < 0 || index >= items.length) {
    return setOrderFieldToIndex([...items, newItem()]);
  }

  const rowsBefore = items.slice(0, index);
  const rowsAfter = items.slice(index);
  return setOrderFieldToIndex([...rowsBefore, newItem(), ...rowsAfter]);
}

function setOrderFieldToIndex<T extends { id: string; order: number }>(
  items: T[],
): T[] {
  return items.map((item, idx) => ({ ...item, order: idx }));
}

export {
  ETable,
  ETableHeader,
  ETableBody,
  ETableFooter,
  ETableHead,
  ETableRow,
  ETableCell,
  ETableCellCheckbox,
  ETableCellInput,
  ETableCellDecimalInput,
  ETableCellSelect,
  ETableCellMultiSelect,
  ETableCellTextarea,
  ETableCellDatePicker,
  ETableCaption,
  ETableOptions,
  type ETableOptionsProps,
  setOrderFieldToIndex,
};
